/**
 * ChanceRadarEditor.js
 * Set up one Chance Radar: a template or a free hunt, how many chances,
 * how recent they have to be, then a live test on one contact.
 */

import React, { useEffect, useState } from "react";
import { ref, push, update, remove } from "firebase/database";
import { database } from "../firebaseConfig";
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from "../utils/cloudFunctions";
import { costOf } from "../config/creditPlans";
import { loadDemoContacts, pickRandomContacts } from "./salesPlayDemo";
import { buildImageContextText, buildImagePromptVariables } from "./EmailAiModes/dynamicImageUtils";
import ChanceRadarView from "../chanceRadar/ChanceRadarView";
import {
  CHANCE_RADII,
  CHANCE_TEMPLATES,
  CHANCE_WINDOWS,
  queryForHunt,
  templateById,
} from "./chanceRadarTemplates";
import "./ImageLab.css";
import "./MiniAppEditor.css";
import "./ChanceRadarEditor.css";

const callChanceRadar = async (payload) => {
  const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.chanceRadar(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok) return data;
  if (res.status === 402) {
    throw new Error(
      `Not enough credits (needs ${data.creditsRequired ?? "a few"}, balance ${data.creditBalance ?? 0}).`,
    );
  }
  throw new Error(data.details || data.error || "Chance Radar failed.");
};

const Choice = ({ label, value, options, onChange }) => (
  <div className="il-field">
    <span className="il-fieldLabel">{label}</span>
    <div className="cr-choice" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          role="radio"
          aria-checked={option.value === value}
          className={option.value === value ? "active" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const ChanceRadarEditor = ({ userId, assetId = null, assets = [], onExit }) => {
  const existing = assets.find((asset) => asset.id === assetId) || null;
  const starter = existing ? templateById(existing.templateId) : CHANCE_TEMPLATES[0];
  const [templateId, setTemplateId] = useState(existing?.templateId || starter.id);
  const [hunt, setHunt] = useState(existing?.hunt || starter.hunt);
  const [topic, setTopic] = useState(existing?.topic || starter.topic);
  const [firstStep, setFirstStep] = useState(existing?.firstStep || starter.firstStep);
  const [count, setCount] = useState(existing?.count || starter.count);
  const [windowDays, setWindowDays] = useState(existing?.windowDays || starter.windowDays);
  const [radiusKm, setRadiusKm] = useState(
    existing?.radiusKm === 0 ? 0 : existing?.radiusKm || starter.radiusKm,
  );
  const [name, setName] = useState(existing?.name || starter.name);
  const [savedId, setSavedId] = useState(existing?.id || null);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [briefing, setBriefing] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let alive = true;
    if (!userId) return undefined;
    loadDemoContacts(userId)
      .then((rows) => {
        if (!alive) return;
        const picked = pickRandomContacts(Array.isArray(rows) ? rows : [], 6);
        setContacts(picked);
        if (picked[0]) setContactId((current) => current || picked[0].id);
      })
      .catch(() => {
        if (alive) setContacts([]);
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  const contact = contacts.find((row) => row.id === contactId) || contacts[0] || null;
  const credits = costOf("chanceRadar");

  const applyTemplate = (template) => {
    setTemplateId(template.id);
    setHunt(template.hunt);
    setTopic(template.topic);
    setFirstStep(template.firstStep);
    setCount(template.count);
    setWindowDays(template.windowDays);
    setRadiusKm(template.radiusKm);
    if (!existing) setName(template.name);
    setBriefing(null);
    setMessage("");
  };

  const run = async () => {
    if (!contact) {
      setMessage("Pick a contact first.");
      return;
    }
    if (hunt.trim().length < 20) {
      setMessage("Describe what a chance is, in at least one sentence.");
      return;
    }
    setBusy("run");
    setMessage("");
    setBriefing(null);
    try {
      const data = await callChanceRadar({
        action: "draft",
        userId,
        contactId: contact.id,
        token: savedId || "draft",
        configId: savedId || "",
        hunt: hunt.trim(),
        topic,
        firstStep,
        count,
        windowDays,
        radiusKm,
        query: queryForHunt(templateId, hunt),
        variables: buildImagePromptVariables(contact, {}),
        contextText: buildImageContextText(contact),
      });
      if (data.empty) {
        setMessage(
          data.briefing?.notice
            ? `${data.briefing.notice} Nothing verified this time, so it was not charged.`
            : "Nothing verified in this window. The search was not charged.",
        );
        return;
      }
      setBriefing(data.briefing);
      setMessage(
        data.cached
          ? "Reused the search from the last 21 days."
          : `Found ${data.briefing?.chances?.length || 0} chances. ${data.credits || credits} credits.`,
      );
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    if (hunt.trim().length < 20) {
      setMessage("Describe what a chance is, in at least one sentence.");
      return;
    }
    setBusy("save");
    setMessage("");
    const now = Date.now();
    const payload = {
      type: "chanceRadar",
      name: name.trim() || topic || "Chance Radar",
      templateId,
      topic,
      hunt: hunt.trim(),
      firstStep: firstStep.trim(),
      query: queryForHunt(templateId, hunt),
      count,
      windowDays,
      radiusKm,
      updatedAt: now,
    };
    try {
      const basePath = `/World/${userId}/settings/chanceRadars`;
      if (savedId) {
        await update(ref(database, `${basePath}/${savedId}`), payload);
      } else {
        const created = push(ref(database, basePath));
        await update(created, { ...payload, token: created.key, configId: created.key, createdAt: now });
        setSavedId(created.key);
      }
      setMessage("Saved. Turn it on for a template, or add it as a step in an agent. The findings go into the email context.");
    } catch (err) {
      setMessage(err.message || "Could not save.");
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async () => {
    if (!savedId || !window.confirm("Delete this Chance Radar? Findings already stored on contacts stay until the next run.")) return;
    await remove(ref(database, `/World/${userId}/settings/chanceRadars/${savedId}`));
    onExit();
  };

  const windowOptions = CHANCE_WINDOWS.map((days) => ({
    value: days,
    label: days === 180 ? "6 months" : `${days} days`,
  }));

  return (
    <div className="il wr">
      <div className="il-editorBar">
        <span className="il-editorTitle">Chance Radar</span>
      </div>

      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">1</span>
          <div>
            <h3 className="wr-stepTitle">What to look for</h3>
            <p className="wr-stepSub">
              A template fills the hunt. Rewrite it and the search follows your words instead of the canned query.
            </p>
          </div>
        </div>
        <div className="cr-templates" role="radiogroup" aria-label="Chance Radar templates">
          {CHANCE_TEMPLATES.map((template) => (
            <button
              type="button"
              key={template.id}
              role="radio"
              aria-checked={template.id === templateId}
              className={`cr-template ${template.id === templateId ? "active" : ""}`}
              onClick={() => applyTemplate(template)}
            >
              <b>{template.name}</b>
              <span>{template.pitch}</span>
              {template.id === templateId && <i className="fa-solid fa-circle-check" aria-hidden="true" />}
            </button>
          ))}
        </div>
        <div className="mae-fields" style={{ marginTop: 16 }}>
          <label className="il-field">
            <span className="il-fieldLabel">Hunt</span>
            <textarea
              className="il-aiPromptInput"
              rows={5}
              value={hunt}
              placeholder="Which live signal would be a chance for this target?"
              onChange={(event) => setHunt(event.target.value)}
            />
          </label>
          <label className="il-field">
            <span className="il-fieldLabel">First step for their sales team</span>
            <textarea
              className="il-aiPromptInput"
              rows={2}
              value={firstStep}
              onChange={(event) => setFirstStep(event.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">2</span>
          <div>
            <h3 className="wr-stepTitle">How many, how recent</h3>
            <p className="wr-stepSub">Newer signals are listed first. 7 days is the sharpest list.</p>
          </div>
        </div>
        <div className="cr-row">
          <Choice
            label="Chances"
            value={count}
            options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))}
            onChange={setCount}
          />
          <Choice label="Window" value={windowDays} options={windowOptions} onChange={setWindowDays} />
          <Choice
            label="Area"
            value={radiusKm}
            options={CHANCE_RADII.map((radius) => ({ value: radius.km, label: radius.label }))}
            onChange={setRadiusKm}
          />
        </div>
        <p className="cr-note">
          One cheaper Google search per contact, about {credits} credit, reused for 21 days. It can include a weak hit. Nothing verified is not charged. Newer signals are listed first.
        </p>
      </section>

      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">3</span>
          <div>
            <h3 className="wr-stepTitle">Try it, then save</h3>
            <p className="wr-stepSub">
              The result is text. A template can add it to every variant, or an agent can run it as a step. It is not an image in the email.
            </p>
          </div>
        </div>
        <div className="cr-contacts" role="radiogroup" aria-label="Test contact">
          {contacts.map((row) => (
            <button
              type="button"
              key={row.id}
              role="radio"
              aria-checked={contact?.id === row.id}
              className={contact?.id === row.id ? "active" : ""}
              onClick={() => setContactId(row.id)}
            >
              {row.business_name}
              {row.lead_city ? <em> · {row.lead_city}</em> : null}
            </button>
          ))}
        </div>
        {!contact?.lead_city && contact && radiusKm > 0 && (
          <p className="cr-note">This contact has no city, so the search covers the country and says so on the page.</p>
        )}
        <div className="cr-actions">
          <button type="button" className="altBtn altBtn--petrol" onClick={run} disabled={busy === "run"}>
            {busy === "run" ? "Looking…" : "Find chances"}
          </button>
        </div>
        <div className="mae-browser cr-browser">
          <div className="mae-browserBar">
            <span />
            <span />
            <span />
            <em>{briefing ? briefing.title : "bonusclub.link/chance/…"}</em>
          </div>
          <div className="mae-browserBody">
            {busy === "run" ? (
              <div className="cr-empty">
                <i className="fa-solid fa-satellite-dish fa-fade" />
                <span>Looking for live chances for {contact?.business_name || "this contact"}…</span>
              </div>
            ) : briefing ? (
              <ChanceRadarView briefing={briefing} />
            ) : (
              <div className="cr-empty">
                <i className="fa-solid fa-satellite-dish" />
                <span>Pick a contact and find chances. Newest signals show up first.</span>
              </div>
            )}
          </div>
        </div>

        <div className="cr-row" style={{ marginTop: 18 }}>
          <label className="il-field">
            <span className="il-fieldLabel">Name</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        </div>
        {briefing?.notice && <p className="cr-note">{briefing.notice}</p>}
        {message && (
          <p className="cr-msg">
            <strong>{message}</strong>
          </p>
        )}
        <div className="il-editorActions">
          <div className="il-editorActionsBtns">
            {savedId && (
              <button type="button" className="il-btnGhost wr-delete" onClick={handleDelete} disabled={!!busy}>
                <i className="fa-regular fa-trash-can" /> Delete
              </button>
            )}
            <button type="button" className="altBtn altBtn--save" onClick={save} disabled={!!busy}>
              {busy === "save" ? "Saving…" : "Save Chance Radar"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChanceRadarEditor;
